import React, {CSSProperties} from 'react'
import ReactDOM from 'react-dom'
import {cn} from '@containers/html-utils'
import {MessageBoxType} from '../types/message-box'
import {
  ToastDefaultIcon,
  ToastErrorIcon,
  ToastSuccessIcon,
  ToastWarningIcon
} from './common/icons/icons'
interface Props {
  text: string
  type: MessageBoxType
  modern?: boolean
  // use -1 for infinity timer
  timer?: number
  children?: React.ReactElement
  style?: CSSProperties
}

export class MessageBox extends React.Component<Props> {
  private static element: HTMLElement
  private static elementModern: HTMLElement

  private timerId: TimeoutHandler

  public static init(): void {
    if (MessageBox.element) {
      return
    }
    const el = document.createElement('div')
    el.className = 'message-box fade'
    document.body.appendChild(el)
    MessageBox.element = el
    MessageBox.show('', 'success')
  }

  public static initModern(): void {
    if (MessageBox.elementModern) {
      return
    }
    const el = document.createElement('div')
    el.className = 'message-box-modern fade'
    document.body.appendChild(el)
    MessageBox.elementModern = el
    MessageBox.show('', 'success', {modern: true})
  }

  public static show(
    text: string,
    type: MessageBoxType,
    opts?: {
      modern?: boolean
      timer?: number
      children?: React.ReactElement
      style?: CSSProperties
    }
  ): void {
    ReactDOM.render(
      <MessageBox
        text={text}
        type={type}
        modern={opts?.modern}
        timer={opts?.timer}
        children={opts?.children}
        style={opts?.style}
      />,
      opts?.modern ? MessageBox.elementModern : MessageBox.element
    )
  }

  public static warn(
    text: string,
    opts?: {
      modern?: boolean
      timer?: number
      children?: React.ReactElement
      style?: CSSProperties
    }
  ): void {
    this.show(text, 'warning', opts)
  }

  public static danger(
    text: string,
    opts?: {
      modern?: boolean
      timer?: number
      children?: React.ReactElement
      style?: CSSProperties
    }
  ): void {
    this.show(text, 'danger', opts)
  }

  public static success(
    text: string,
    opts?: {
      modern?: boolean
      timer?: number
      children?: React.ReactElement
      style?: CSSProperties
    }
  ): void {
    this.show(text, 'success', opts)
  }

  public close = () => {
    MessageBox.element.classList.add('fade')
  }

  public closeModern = () => {
    MessageBox.elementModern.classList.add('fade')
  }

  private fade = (value = true) => {
    if (value) {
      this.props.modern
        ? MessageBox.elementModern.classList.add('fade')
        : MessageBox.element.classList.add('fade')
    } else {
      this.props.modern
        ? MessageBox.elementModern.classList.remove('fade')
        : MessageBox.element.classList.remove('fade')
    }
  }

  private getIcon = () => {
    if (this.props.modern) {
      return this.props.type === 'success' ? (
        <i className="fa fa-check" />
      ) : (
        <i className="fa fa-warning" />
      )
    }

    const Component = {
      success: ToastSuccessIcon,
      warning: ToastWarningIcon,
      danger: ToastErrorIcon,
      default: ToastDefaultIcon
    }[this.props.type]

    return <Component />
  }

  public override render(): React.ReactNode {
    if (this.props.text === '') {
      this.fade()
    } else {
      const autoCloseTimeout = this.props.timer
        ? Math.max(0, this.props.timer)
        : Math.min(15_000, Math.max(5000, this.props.text.length * 50))
      this.fade(false)
      if (this.timerId) {
        clearTimeout(this.timerId)
      }
      if (autoCloseTimeout) {
        this.timerId = setTimeout(this.fade, autoCloseTimeout)
      }
    }

    const icon = this.getIcon()

    return (
      <div
        className={cn(
          this.props.modern
            ? 'message-box-modern-cnt message-box-' + this.props.type
            : 'message-box-cnt message-box-' + this.props.type
        )}
        style={this.props.style}
      >
        <span className="message-box-icon">{icon}</span>
        <span className="message-box-text">
          {this.props.children ?? this.props.text}
        </span>
        <div
          className={cn('message-box-close', this.props.modern && 'h-32')}
          onClick={this.props.modern ? this.closeModern : this.close}
        />
      </div>
    )
  }
}
